// AriaStateVisualizer — the visual representation of ARIA's state.
//
// Not a cartoon. Not a robot avatar. A calm, sophisticated intelligence
// core: SVG circle with pulsing rings, hue-driven by state.
//
// Uses sixfigure.aria tokens (Phase 2 design system) for hue +
// intensity. Every state has a distinct visual signature per the
// Master Directive.
//
// Performance notes (modern-web-design skill):
//   - All animation is CSS keyframes on transform + opacity, no JS RAF.
//     GPU-accelerated. No layout thrash.
//   - Reduced-motion respected via CSS media query.
//   - Static SVG geometry; only the class name changes on state
//     transitions, so React reconciliation is minimal.

import React from 'react';
import { sixfigure } from '../lib/tokens';
import { useAria } from './AriaContext';

const CSS = `
@keyframes aria-pulse-slow {
  0%, 100% { opacity: 0.18; transform: scale(0.94); }
  50%      { opacity: 0.30; transform: scale(1.02); }
}
@keyframes aria-wave {
  0%   { opacity: 0.15; transform: scale(0.90); }
  40%  { opacity: 0.55; transform: scale(1.20); }
  100% { opacity: 0;    transform: scale(1.60); }
}
@keyframes aria-orbit {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes aria-scan {
  0%, 100% { transform: translateY(-60%); opacity: 0; }
  10%      { opacity: 0.6; }
  50%      { transform: translateY(60%); opacity: 0.6; }
  60%      { opacity: 0; }
}
@keyframes aria-focus {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 currentColor; }
  50%      { transform: scale(1.08); box-shadow: 0 0 0 6px transparent; }
}
@keyframes aria-attend {
  0%, 100% { opacity: 0.85; }
  50%      { opacity: 1; }
}
@keyframes aria-urgent {
  0%, 100% { opacity: 1; transform: scale(1); }
  25%      { opacity: 0.6; transform: scale(0.94); }
  75%      { opacity: 1; transform: scale(1.04); }
}
@keyframes aria-confirm {
  0%   { transform: scale(0.6); opacity: 0; }
  60%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes aria-voice {
  0%, 100% { transform: scaleY(0.5); }
  25%      { transform: scaleY(1); }
  50%      { transform: scaleY(0.7); }
  75%      { transform: scaleY(0.9); }
}

.aria-ring {
  transform-origin: center;
  will-change: transform, opacity;
}

.aria-idle     .aria-ring { animation: aria-pulse-slow  3.6s ease-in-out infinite; }
.aria-listening .aria-ring { animation: aria-wave        1.4s ease-out    infinite; }
.aria-thinking .aria-orbit { animation: aria-orbit       2.4s linear      infinite; }
.aria-analyzing .aria-scan { animation: aria-scan        2.0s ease-in-out infinite; }
.aria-found    .aria-core { animation: aria-focus        1.2s ease-out    infinite; }
.aria-warning  .aria-core { animation: aria-attend       1.0s ease-in-out infinite; }
.aria-critical .aria-core { animation: aria-urgent       0.8s ease-in-out infinite; }
.aria-complete .aria-core { animation: aria-confirm      0.6s ease-out    forwards; }
.aria-speaking .aria-voice-bar { animation: aria-voice   0.5s ease-in-out infinite; }

@media (prefers-reduced-motion: reduce) {
  .aria-ring, .aria-orbit, .aria-scan, .aria-core, .aria-voice-bar {
    animation: none !important;
  }
}
`;

export default function AriaStateVisualizer({ size = 48, onClick }) {
  const { state } = useAria();
  const cfg = sixfigure.aria[state] || sixfigure.aria.idle;

  return (
    <>
      <style>{CSS}</style>
      <button
        onClick={onClick}
        aria-label={`ARIA ${cfg.label}`}
        title={`ARIA ${cfg.label}`}
        className={`aria-${state}`}
        style={{
          position: 'relative',
          width: size, height: size,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: cfg.hue,
          display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        {/* Outer pulse ring */}
        <span
          className="aria-ring"
          style={{
            position: 'absolute', inset: 0,
            borderRadius: '50%',
            background: `radial-gradient(circle at center, ${cfg.hue}55 0%, transparent 65%)`,
            opacity: cfg.intensity,
          }}
        />

        {/* Orbit indicator (thinking) */}
        {state === 'thinking' && (
          <span
            className="aria-orbit"
            style={{
              position: 'absolute', inset: 4,
              borderRadius: '50%',
              border: `1.5px solid ${cfg.hue}`,
              borderTopColor: 'transparent',
              borderRightColor: `${cfg.hue}88`,
            }}
          />
        )}

        {/* Scan bar (analyzing) */}
        {state === 'analyzing' && (
          <span
            className="aria-scan"
            style={{
              position: 'absolute', left: '15%', right: '15%',
              height: 2,
              background: `linear-gradient(90deg, transparent, ${cfg.hue}, transparent)`,
              boxShadow: `0 0 8px ${cfg.hue}`,
              borderRadius: 2,
            }}
          />
        )}

        {/* Voice bars (speaking) */}
        {state === 'speaking' && (
          <span style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 3,
          }}>
            {[0, 1, 2, 3].map(i => (
              <span key={i}
                className="aria-voice-bar"
                style={{
                  width: 2, height: 14,
                  background: cfg.hue,
                  borderRadius: 1,
                  animationDelay: `${i * 90}ms`,
                  transformOrigin: 'center',
                }}
              />
            ))}
          </span>
        )}

        {/* Core */}
        <span
          className="aria-core"
          style={{
            position: 'relative',
            width: size * 0.32, height: size * 0.32,
            borderRadius: '50%',
            background: `radial-gradient(circle at 35% 30%, #ffffff 0%, ${cfg.hue} 55%, ${cfg.hue}dd 100%)`,
            boxShadow: `0 0 12px ${cfg.hue}88`,
          }}
        />
      </button>
    </>
  );
}
